import { AppLayoutProps } from "@amzn/awsui-components-react/polaris/app-layout";
import { useEffect, useMemo, useState } from "react";

import chatbotConfig from "./chatbot-config.json"
import { getString } from "../../common/ui-string-labels/ui-strings-utils";
import Drawer from "@amzn/awsui-components-console/drawer";
import ChatbotContent from "./ChatbotContent";
import { DrawerChangeEvent } from "./interfaces";

/** Subscribes to a CSS media query and re-renders when the match state changes (on resize). */
const useMediaQuery = (query: string) => {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [query]);
    return matches;
};

const useAppDrawers = () => {
    const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
    const isSmallScreen = useMediaQuery(`(max-width: ${chatbotConfig.smallScreenBreakpoint}px)`);

    const drawers = useMemo<AppLayoutProps.Drawer[]>(() =>
        isSmallScreen ? [] : [
            {
                id: "chatbot",
                content: (
                    <Drawer disableContentPaddings header={getString('chatbot.drawer.header')}>
                        <ChatbotContent />
                    </Drawer>
                ),
                trigger: {
                    iconName: 'gen-ai'
                },
                badge: false,
                ariaLabels: {
                    drawerName: getString('chatbot.drawer.drawerName'),
                    closeButton: getString('chatbot.drawer.closeButton'),
                    triggerButton: getString('chatbot.drawer.triggerButton'),
                },
                defaultSize: 380,
                resizable: true,
                preserveInactiveContent: true,
            },
        ], [isSmallScreen]);

    return {
        drawers,
        activeDrawerId,
        onDrawerChange: (event: DrawerChangeEvent) => {
            setActiveDrawerId(event.detail.activeDrawerId);
        },
    };
};

export default useAppDrawers;
